import { AlertService } from '@/services/alert.service';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Location, PropertiesService, RealProperty, RealPropertySummaryLocation } from '@eemo/api-client';
import * as L from 'leaflet';
import { Observable, Subscription } from 'rxjs';
import { LayerType } from '../../enums/LayerType.enum';
import { InitDataCoord } from '../../interfaces/InitData.interface';
import { MapParcelle } from '../../interfaces/mapParcelle.interface';
import { MapService } from '../../services/MapService.service';

@Component({
  selector: 'app-new-map',
  templateUrl: './new-map.component.html',
  styleUrls: ['./new-map.component.scss'],
  standalone: true
})
export class NewMapComponent implements OnDestroy, OnInit, AfterViewInit {
  @Input() initMapCoord: InitDataCoord | null = { longitude: 5.554907634701757, latitude: 45.41637629836208 };
  @Input() initZoom = 17;
  @Input() hasSatelliteView = true;
  @Input() hasPlanView = false;
  @Input() hasCadastreView = false;
  @Input() hasVectorParcelles: boolean;
  @Input() hasEventParcelles = false;
  @Input() hasLayerControls = true;
  @Input() isLocateEnabled = true;
  @Input() parcelleInputSelected: Observable<string>;
  @Input() parcelleInputUnSelected: Observable<string>;
  @Input() landRegistriesSelectedMax: number | null = null;
  @Input() defaultLayer: LayerType = LayerType.PLAN;
  @Input() propsType: 'full' | 'summary' = 'full';
  @Input() props: RealProperty[] | RealPropertySummaryLocation[];
  @Input() showPropPopup = true;
  @Input() updateLocationEnabled = false;

  @Output() locationUpdated = new EventEmitter<Location>();
  @Output() parcelleSelected = new EventEmitter<MapParcelle>();
  @Output() parcelleUnSelected = new EventEmitter<MapParcelle>();
  @Output() mapServiceInstance = new EventEmitter<MapService>();

  mapService: MapService;
  locationUpdatedSub = new Subscription();
  parcellesSelectionUpdatedSub = new Subscription();
  parcellesUnSelectionUpdatedSub = new Subscription();
  id = 'map-' + Math.random().toString(16).slice(2);

  constructor(
    private propertyService: PropertiesService,
    private alertService: AlertService,
    private c: ChangeDetectorRef
  ) {
    this.mapService = new MapService(propertyService, alertService, this.id);
    this.initSubscriptions();
  }

  ngOnInit(): void {
    if (this.parcelleInputSelected) {
      this.mapService.parcelleInputSelected$ = this.parcelleInputSelected;
    }
    if (this.parcelleInputUnSelected) {
      this.mapService.parcelleInputUnSelected$ = this.parcelleInputUnSelected;
    }
    this.mapServiceInstance.emit(this.mapService);
  }

  ngAfterViewInit(): void {
    if (!this.mapService.isInitialized()) {
      this.handler();
    }
  }

  ngOnDestroy(): void {
    this.locationUpdatedSub.unsubscribe();
    this.parcellesSelectionUpdatedSub.unsubscribe();
    this.parcellesUnSelectionUpdatedSub.unsubscribe();
  }

  initSubscriptions() {
    this.locationUpdatedSub = this.mapService.locationUpdated$.subscribe((res: Location) => {
      this.locationUpdated.emit(res);
    });
    this.parcellesSelectionUpdatedSub = this.mapService.parcelleSelected$.subscribe((res: MapParcelle) => {
      this.parcelleSelected.emit(res);
    });
    this.parcellesUnSelectionUpdatedSub = this.mapService.parcelleUnSelected$.subscribe((res: MapParcelle) => {
      this.parcelleUnSelected.emit(res);
    });
  }

  handler(isReload = false) {
    if (isReload) {
      this.mapService.clear();
    }
    this.mapService.init(
      this.initMapCoord.longitude,
      this.initMapCoord.latitude,
      this.initZoom,
      this.landRegistriesSelectedMax
    );

    const controlList: any[] = [];

    // main layers
    if (this.hasSatelliteView) {
      const satellite = this.mapService.generateSatelliteLayer();
      controlList.push(this.mapService.mapLayerControlFromLayer(LayerType.SATELLITE, satellite));
      this.mapService.setDefaultLayer(this.defaultLayer, LayerType.SATELLITE, satellite);
    }
    if (this.hasPlanView) {
      const plan = this.mapService.generatePlanLayer();
      controlList.push(this.mapService.mapLayerControlFromLayer(LayerType.PLAN, plan));
      this.mapService.setDefaultLayer(this.defaultLayer, LayerType.PLAN, plan);
    }
    if (this.hasCadastreView) {
      const cadastre = this.mapService.generateCadastreLayer();
      controlList.push(this.mapService.mapLayerControlFromLayer(LayerType.CADASTRE, cadastre));
      this.mapService.setDefaultLayer(this.defaultLayer, LayerType.CADASTRE, cadastre);
    }
    // other layers
    if (this.hasVectorParcelles || this.hasEventParcelles) {
      this.mapService.hasEventParcelles = this.hasEventParcelles;
      this.mapService.addParcelleVectorLayer();
      // select the registries for the first prop
      if (this.propsType === 'full' && this.props.length > 0) {
        const prop = this.props[0] as RealProperty;
        if (prop.landRegistry?.identifier) {
          this.mapService.updateParcelleInputSelect(prop.landRegistry.identifier);
        }
        if (prop.otherLandRegistries) {
          prop.otherLandRegistries.forEach((r) => {
            if (r.identifier) {
              this.mapService.updateParcelleInputSelect(r.identifier);
            }
          });
        }
      }
    }

    // controls
    if (this.isLocateEnabled) {
      this.mapService.addLocation();
    }
    this.mapService.addZoomControls();

    if (this.hasLayerControls) {
      this.mapService.addLayerControls(controlList);
    }

    // markers
    this.mapService.addMarkers(this.props, this.propsType, this.updateLocationEnabled, this.showPropPopup);

    this.cropMap();
  }

  // default: {longitude: 5.554907634701757, latitude: 45.41637629836208}
  cropMap() {
    if (this.props.length === 1) {
      const prop: RealProperty = this.props[0];
      this.mapService.map.setZoom(this.initZoom);
      this.mapService.map.panTo(L.latLng(prop.location.latitude, prop.location.longitude));
    } else {
      const mapBounds = this.getMapBounds();
      this.mapService.map.fitBounds(mapBounds.bounds);
      this.mapService.map.panTo(mapBounds.center);
    }
  }

  getMapBounds(): { bounds: L.LatLngBounds; center: L.LatLng } {
    let minLat: number = null;
    let maxLat: number = null;
    let minLon: number = null;
    let maxLon: number = null;

    this.props.forEach((prop) => {
      if (prop.location) {
        if (minLat === null || prop.location.latitude < minLat) {
          minLat = prop.location.latitude;
        }
        if (maxLat === null || prop.location.latitude > maxLat) {
          maxLat = prop.location.latitude;
        }
        if (minLon === null || prop.location.longitude < minLon) {
          minLon = prop.location.longitude;
        }
        if (maxLon === null || prop.location.longitude > maxLon) {
          maxLon = prop.location.longitude;
        }
      }
    });
    if (isNaN(minLat)) {
      L.latLngBounds(L.latLng(0, 0), L.latLng(0, 0));
    }
    return {
      bounds: L.latLngBounds(L.latLng(minLat, minLon), L.latLng(maxLat, maxLon)),
      center: L.latLng((minLat + maxLat) / 2, (minLon + maxLon) / 2)
    };
  }

  reloadMap() {
    this.handler(true);
  }
}
